import * as PIXI from "pixi.js";

import { EGameState, TSpinResponse } from "@src/server/ResponseTypes";
import { TGameAssetAlias } from "@src/game/screens/GameScreen";
import { EventSystem } from "@src/core/events/EventSystem";
import { TGameUIButtonType } from "@src/game/ui/GameUI";
import { IView } from "@src/core/game/components/IView";
import { Symbol } from "@src/game/symbol-panel/Symbol";
import { Assets } from "@src/core/assets/Assets";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";
import { CoinAnimator } from "../symbol-panel/components/CoinAnimator";

abstract class CellView implements IView {
  protected _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  protected _symbol: Symbol | undefined;
  public get symbol(): Symbol | undefined {
    return this._symbol;
  }

  public backgroundSprite!: PIXI.Sprite;

  public abstract createSymbol(type: string): void;
  public abstract createOrRandomizeSymbol(): void;
  protected abstract _addBackgroundSprite(): void;

  public constructor(type: string = "") {
    this.createSymbol(type);
    this._addBackgroundSprite();
  }

  public destroy(): void {
    this.releaseSymbol();
    this._view.removeChild(this.backgroundSprite);
    this._view.removeFromParent();
  }

  public releaseSymbol(): void {
    if (this._symbol) {
      this._view.removeChild(this._symbol.view);
      Symbol.pool.release(this.symbol!);
      this._symbol = undefined;
    }
  }
}

export class SymbolCellView extends CellView {
  public override createSymbol(type: string): void {
    if (!type.startsWith("100") || this._symbol) {
      return;
    }

    let symbol: Symbol = Symbol.pool.acquire();

    const coinMultiplierText: string = type.split("_")[1];
    symbol.setType("100");
    symbol.setCoinMultiplierText(coinMultiplierText);

    symbol.view.scale.set(1.15);

    this._symbol = symbol;
    this._view.addChild(this._symbol.view);
  }

  public override createOrRandomizeSymbol(): void {
    if (this._symbol) {
      if (Math.random() > 0.5) {
        this.releaseSymbol();
      }
    } else {
      this.createSymbol(
        Math.random() > 0.5
          ? `100_${this._getRandomCellSymbolMultiplierValue()}`
          : "",
      );
    }
  }

  protected override _addBackgroundSprite(): void {
    this.backgroundSprite = new PIXI.Sprite(
      Assets.getAsset<TGameAssetAlias>("cellImg"),
    );
    this.backgroundSprite.scale.set(0.7);
    this.backgroundSprite.anchor.set(0.5);
    this.backgroundSprite.position.set(0);

    this._view.addChild(this.backgroundSprite);
  }

  public _getRandomCellSymbolMultiplierValue(): string {
    const multipliers: string[] = [
      "1",
      "2",
      "3",
      "5",
      "8",
      "10",
      "20",
      "50",
      "100",
    ];
    const multiplier: string =
      multipliers[Math.floor(Math.random() * multipliers.length)];

    return multiplier;
  }
}

export class SpecialCellView extends CellView {
  private _text: PIXI.Text = new PIXI.Text({
    text: "",
    style: {
      fontSize: 30,
      fontFamily: "gameFont",
      align: "center",
      fill: 0xffbb1e,
      stroke: {
        color: 0x000000,
        width: 7,
        join: "round",
      },
    }
  });

  public override createSymbol(type: string): void {
    if (!(type === "11" || type === "12" || type === "13") || this._symbol) {
      return;
    }

    let symbol: Symbol = Symbol.pool.acquire();

    symbol.view.scale.set(1.35);
    symbol.view.position.set(7, 13);
    symbol.setType(type);

    this._symbol = symbol;
    this._view.addChild(this._symbol.view);
    this._view.addChild(this._text);
    this._text.anchor.set(0.5);
    this._text.zIndex = 100;

    switch (type) {
      case "11":
        this._text.position.set(35.0, 40.0);
        this._text.text = "x2";
        break;
      case "12":
        this._text.position.set(27.0, 40.0);
        this._text.text = "+lvl";
        break;
      case "13":
        this._text.position.set(28.0, 40.0);
        this._text.text = "sum";
        break;
    }
  }

  public override createOrRandomizeSymbol(): void {
    if (this._symbol) {
      if (Math.random() > 0.5) {
        this.releaseSymbol();
      }
    } else {
      const randomSymbolType: string[] = ["11", "12", "13"];
      this.createSymbol(
        randomSymbolType[Math.floor(Math.random() * randomSymbolType.length)],
      );
    }
    this._text.text = "";
  }

  protected override _addBackgroundSprite(): void {
    this.backgroundSprite = new PIXI.Sprite(
      Assets.getAsset<TGameAssetAlias>("bagCellImg"),
    );
    this.backgroundSprite.scale.set(0.7);
    this.backgroundSprite.anchor.set(0.5);
    this.backgroundSprite.position.set(0);

    this._view.addChild(this.backgroundSprite);
  }
}

// Cells include symbols, but they are independent units
// and spin in their own constraints.
// Cells include 2 TCellView-s that come into player's view
// interchangeably during spin until stopped.
export class Cell implements IView {
  public static readonly WORLD_Y_OFFSET: number = 144 as const;

  public static readonly WIDTH: number = 144 as const;
  public static readonly HEIGHT: number = 144 as const;

  public static readonly STOP_DELAY_S: number = 0.2 as const;
  public static readonly STOP_DURATION_S: number = 0.2 as const;
  public static readonly SWIFT_STOP_DURATION_S: number = 0.3 as const;

  // Time in which the reel spins by the height of a single symbol.
  public static readonly SPIN_ITERATION_DURATION_S: number = 0.2 as const;
  public static readonly SPIN_DURATION_INITIAL_S: number = 0.4 as const;
  public static readonly SPIN_DURATION_TOTAL_S: number = 1.5 as const;
  public static readonly SPIN_DELAY_S: number = 0.08 as const;

  public static readonly ONE_SYMBOL_SCROLL_TARGET_Y: number =
    Cell.HEIGHT + Cell.WORLD_Y_OFFSET;

  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _cellMaskView: PIXI.Container = new PIXI.Container();
  public get cellMaskView(): PIXI.Container {
    return this._cellMaskView;
  }

  private _heldSymbol!: Symbol | undefined;
  public get heldSymbol(): Symbol | undefined {
    return this._heldSymbol;
  }

  public get isHeld(): boolean {
    return this._heldSymbol !== undefined;
  }

  private static _stoppedCellCount: number = 0;
  private static _gameActionDataReceivedCellCount: number = 0;
  private static _heldSymbolCount: number = 0;

  private static get _unheldSymbolCount(): number {
    return (
      gameEngine.config.reels * gameEngine.config.rows - Cell._heldSymbolCount
    );
  }

  private _mask!: PIXI.Graphics;

  private _reelPlayAnimationTimeline!: GSAPTimeline;
  private _reelStopAnimationTimeline!: GSAPTimeline;

  private _type: "symbol" | "special";
  private _reelIndex!: number;
  private _rowIndex!: number;
  private _cellViews!: CellView[];

  private _isSlamStopping: boolean = false;
  // This property is only relevant before a spin is started.
  private _isSwiftButtonEnabled: boolean = false;

  private _boundOnPerformedGameAction!: (response: TSpinResponse) => void;
  private _boundOnCellStopped!: (_: number, __: number) => void;
  private _boundOnCellStopRequested!: (_: number, __: number) => void;
  private _boundOnSpinStopped!: () => void;
  private _boundOnCellPanelShowAnimationStarted!: () => void;
  private _boundOnCellPanelHidden!: () => void;
  private _boundOnClickedSwiftButton!: (_: boolean) => void;
  private _boundOnClickedInteractableArea!: () => void;
  private _boundOnClickedUIButton!: (_: TGameUIButtonType) => void;
  private _boundOnClickedRigButton!: (_: string) => void;

  public constructor(
    type: "symbol" | "special",
    reelIndex: number,
    rowIndex: number,
  ) {
    this._type = type;
    this._reelIndex = reelIndex;
    this._rowIndex = rowIndex;

    this._onCellAnimationRequested = this._onCellAnimationRequested.bind(this);
    this._boundOnPerformedGameAction = this._onPerformedGameAction.bind(this);
    this._boundOnCellStopped = this._onCellStopped.bind(this);
    this._boundOnCellStopRequested = this._onCellStopRequested.bind(this);
    this._boundOnSpinStopped = this._onSpinStopped.bind(this);
    this._boundOnCellPanelShowAnimationStarted =
      this._onCellPanelShowAnimationStarted.bind(this);
    this._boundOnCellPanelHidden = this._onCellPanelHidden.bind(this);
    this._boundOnClickedSwiftButton = this._onClickedSwiftButton.bind(this);
    this._boundOnClickedInteractableArea =
      this._onClickedInteractableArea.bind(this);
    this._boundOnClickedUIButton = this._onClickedUIButton.bind(this);
    this._boundOnClickedRigButton = this._onClickedRigButton.bind(this);

    // These two events do not need removal for the entire lifetime of the Cell object.
    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SHOW_ANIMATION_STARTED,
      this._boundOnCellPanelShowAnimationStarted,
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_HIDDEN,
      this._boundOnCellPanelHidden,
    );
  }

  public static getCenteredCellPositionY(row: number): number {
    let offset: number = 0;
    if (row === -1) {
      offset = -13;
    }
    return Cell.HEIGHT * row + Cell.HEIGHT / 2 - Cell.WORLD_Y_OFFSET + offset;
  }

  private _initializeCell(): void {
    this._cellViews = [];

    const symbolType: string =
      gameEngine.gameActionData.panelNew[this._reelIndex][this._rowIndex];

    const newCellView: CellView =
      this._type === "symbol"
        ? new SymbolCellView(symbolType)
        : new SpecialCellView(symbolType);

    this._view.addChild(newCellView.view);
    this._heldSymbol = newCellView.symbol;
    this._cellViews.push(newCellView);

    if (symbolType && symbolType.startsWith("100")) {
      Cell._heldSymbolCount++;
    }

    this._initializeComponents();
    this._parentComponents();

    this._view.mask = this._mask;

    this._setupEventListeners();
  }

  private _resetCell(): void {
    Cell._stoppedCellCount = 0;
    Cell._gameActionDataReceivedCellCount = 0;
    Cell._heldSymbolCount = 0;
    Cell._animatingReels = [];

    this._cellViews.forEach((cellView) => {
      if (cellView.symbol) {
        Symbol.pool.release(cellView.symbol);
        this._view.removeChild(cellView.symbol.view);
      }
      this._view.removeChild(cellView.backgroundSprite);
    });
    this._cellViews = [];
    this._heldSymbol = undefined;

    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();

    this._view.mask = null;

    this._removeEventListeners();
  }

  private _initializeComponents(): void {
    this._mask = new PIXI.Graphics();
    this._mask
      .rect(-Cell.WIDTH / 2, -Cell.HEIGHT / 2, Cell.WIDTH, Cell.HEIGHT)
      .fill({ color: 0xffffff, alpha: 0 });
  }

  private _parentComponents(): void {
    this._cellMaskView.addChild(this._mask);
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.GameEvents.CELL_ANIMATION_REQUESTED,
      this._onCellAnimationRequested,
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._boundOnPerformedGameAction,
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_STOPPED,
      this._boundOnCellStopped,
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_STOP_REQUESTED,
      this._boundOnCellStopRequested,
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SPIN_STOPPED,
      this._boundOnSpinStopped,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_SWIFT_BUTTON,
      this._boundOnClickedSwiftButton,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._boundOnClickedInteractableArea,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._boundOnClickedUIButton,
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._boundOnClickedRigButton,
    );
  }

  private _removeEventListeners(): void {
    EventSystem.removeListener(
      constants.GameEvents.GAME_PERFORM_ACTION,
      this._boundOnPerformedGameAction,
    );

    EventSystem.removeListener(
      constants.GameEvents.CELL_STOPPED,
      this._boundOnCellStopped,
    );

    EventSystem.removeListener(
      constants.GameEvents.CELL_STOP_REQUESTED,
      this._boundOnCellStopRequested,
    );

    EventSystem.removeListener(
      constants.GameEvents.CELL_PANEL_SPIN_STOPPED,
      this._boundOnSpinStopped,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_SWIFT_BUTTON,
      this._boundOnClickedSwiftButton,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_INTERACTABLE_AREA,
      this._boundOnClickedInteractableArea,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._boundOnClickedUIButton,
    );

    EventSystem.removeListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._boundOnClickedRigButton,
    );
  }

  private _onPerformedGameAction(_: TSpinResponse): void {
    if (this.isHeld || this._type === "special") {
      return;
    }

    // console.log(
    //   `Cell ${this._reelIndex}-${this._rowIndex} received game action data`,
    // );

    Cell._gameActionDataReceivedCellCount++;
  }

  private _onCellAnimationRequested(reelIndex: number, type: "11" | "12" | "13"): void {
    // console.log(`[CELL ANIMATION] Requested cell animation on reel ${reelIndex}`);
    // -1 to indicate all reels need to be involved.
    if (
      (reelIndex !== -1 && reelIndex !== this._reelIndex)
    ) {
      return;
    }

    if ((!this._heldSymbol) || (this._type === "special")) {
      return;
    }

    gsap.killTweensOf(this._heldSymbol.view);

    gsap.to(this._heldSymbol.view, {
      duration: 0.03,
      delay: 0.12 * this._rowIndex,
      x: "+=5.0",
      repeat: 5,
      yoyo: true,
      ease: "power1.inOut",
      onStart: () => {
        this._heldSymbol!.playWinAnimation();

        const symbolGlobalPos = this._heldSymbol!.view.toGlobal(new PIXI.Point(0, 0));

        EventSystem.invoke(
          constants.GameEvents.SPECIAL_EFFECT_APPLIED,
          symbolGlobalPos.x,
          symbolGlobalPos.y,
        );

        const tiers: string[] = ["1", "2", "3", "5", "8", "10", "20", "50", "100"];
        const multiplierText: string = this._heldSymbol!.getCoinMultiplierText();
        const currentVal = Number(multiplierText);
        let targetVal = currentVal;

        switch (type) {
          case "11":
            targetVal = currentVal * 2;
            break;
          case "12":
            const nextTier = tiers.find(t => Number(t) > currentVal);
            if (nextTier) {
              targetVal = Number(nextTier);
            }

            break;
          case "13":
            break;
        }

        if (targetVal !== currentVal) {
          const proxy = { value: currentVal };

          gsap.to(proxy, {
            value: targetVal,
            duration: 0.4,
            ease: "expo.out",
            onUpdate: () => {
              this._heldSymbol!.setCoinMultiplierText(String(Math.floor(proxy.value)));
            },
            onComplete: () => {
              this._heldSymbol!.setCoinMultiplierText(String(targetVal));
            }
          });
        }
      },
      onComplete: () => {
        const arrayIndex = Cell._animatingReels.indexOf(this._reelIndex);
        if (arrayIndex > -1) {
          Cell._animatingReels.splice(arrayIndex, 1);
        }
      }
    });
  }

  private static _animatingReels: number[] = [];
  public static get animatingReels(): number[] {
    return Cell._animatingReels;
  }

  private _onCellStopped(reelIndex: number, rowIndex: number): void {
    if (
      !(reelIndex === this._reelIndex && rowIndex === this._rowIndex)
    ) {
      return;
    }

    if (this._type === "special") {
      if (this.isHeld) {
        const specialType = this._heldSymbol!.type;
        const targetReel = (specialType === "13") ? -1 : this._reelIndex;

        EventSystem.invoke(
          constants.GameEvents.CELL_ANIMATION_REQUESTED,
          targetReel,
          specialType,
          this._view.worldTransform.tx,
          this._view.worldTransform.ty,
        );
        Cell._animatingReels.push(this._reelIndex);
      }

      return;
    }

    // console.log(`Cell stopped: ${reelIndex}-${rowIndex}`);
    Cell._stoppedCellCount++;

    // console.log(`heldCellCount is ${Cell._heldSymbolCount}`);
    // console.log(
    //   `gameActionDataReceivedCellCount is ${Cell._gameActionDataReceivedCellCount}`,
    // );
    // console.log(`_stoppedCellCount is ${Cell._stoppedCellCount}`);

    if (Cell._stoppedCellCount === Cell._gameActionDataReceivedCellCount) {
      EventSystem.invoke(constants.GameEvents.CELL_PANEL_SPIN_STOPPED);
      Audio.stop("sndSpin");

      Cell._stoppedCellCount = 0;
      Cell._gameActionDataReceivedCellCount = 0;
      if (gameEngine.gameActionData.state === EGameState.HOLD_TO_MAIN) {
        Cell._heldSymbolCount = 0;
      }
    }
  }

  private _onCellStopRequested(reelIndex: number, rowIndex: number): void {
    if (this._reelIndex === reelIndex && this._rowIndex === rowIndex) {
      this._stopSpinAnimation(this._reelIndex, this._rowIndex);
    }
  }

  private _onSpinStopped(): void {
    this._isSlamStopping = false;

    // console.log("all cell spins have stopped");

    if (gameEngine.gameActionData.state === EGameState.HOLD) {
      this._queueSpin();
    }
  }

  private _onCellPanelShowAnimationStarted(): void {
    console.log("initialized cell");
    this._initializeCell();
  }

  private _onCellPanelHidden(): void {
    console.log("initialized cell");
    this._resetCell();
  }

  private _onClickedSwiftButton(enabled: boolean): void {
    this._isSwiftButtonEnabled = enabled;
  }

  private _onClickedInteractableArea(): void {
    this._isSlamStopping = true;
  }

  private _onClickedUIButton(buttonType: TGameUIButtonType): void {
    switch (buttonType) {
      case "spin":
        this._queueSpin();
        break;
    }
  }

  private _onClickedRigButton(_: string): void {
    this._queueSpin();
  }

  private _queueSpinAnimationStart(reelIndex: number, rowIndex: number): void {
    gsap.delayedCall(Cell.SPIN_DELAY_S, () => {
      this._playSpinAnimation(reelIndex, rowIndex);
    });
  }

  // TODO: Handle edge case when the player has to continue their paused game.
  private _queueSpin(): void {
    // if (gameEngine.gameActionData) {
    //     switch (gameEngine.gameActionData.state) {
    //         case EGameState.MAIN:
    //         case EGameState.MAIN_TO_BONUS:
    //         case EGameState.BONUS:
    //         case EGameState.BONUS_TO_MAIN:
    //         case EGameState.BONUS_TO_HOLD:
    //         case EGameState.HOLD_TO_MAIN:
    //             return;
    //     }
    // }

    if (this.isHeld) {
      return;
    }

    // console.log(`spinning ${this._reelIndex}, ${this._rowIndex},
    //     with ${this._cellViews.length} cell views`);

    this._isSlamStopping = false;

    // console.log(
    //   `queueing spin with game mode ${gameEngine.gameActionData.state}`,
    // );
    this._queueSpinAnimationStart(this._reelIndex, this._rowIndex);
  }

  private _isReadyToStop(): boolean {
    // console.log(`unheldCellCount is ${Cell._unheldSymbolCount}`);
    // console.log(
    //   `gameActionDataReceivedCellCount is ${Cell._gameActionDataReceivedCellCount}`,
    // );
    return Cell._gameActionDataReceivedCellCount === Cell._unheldSymbolCount;
  }

  private _isReadyToQuickstop(): boolean {
    return (
      (this._isSlamStopping || this._isSwiftButtonEnabled) &&
      this._isReadyToStop()
    );
  }

  private _createNewCellViewAtIndex(index: number): CellView {
    const newCellView: CellView =
      this._type === "symbol" ? new SymbolCellView() : new SpecialCellView();
    this._view.addChild(newCellView.view);

    newCellView.view.y = index * Cell.HEIGHT - Cell.HEIGHT;
    this._cellViews.splice(index, 0, newCellView);

    this._resetCellViewPositions();

    return newCellView;
  }

  private _releaseCellViewAtIndex(index: number): void {
    const cellView: CellView | undefined = this._cellViews.splice(index, 1)[0];

    if (!cellView) {
      return;
    }

    cellView.destroy();

    this._resetCellViewPositions();
  }

  private _moveBottomCellViewToTop(): void {
    const bottomCellView: CellView | undefined = this._cellViews.pop()!;
    this._cellViews.unshift(bottomCellView);
    this._resetCellViewPositions();
  }

  // if there are >= 2 cell views, position them from top to bottom
  // if there is just one, position it in the middle
  private _resetCellViewPositions(): void {
    if (this._cellViews.length === 0) {
      return;
    }

    if (this._cellViews.length === 1) {
      this._cellViews[0].view.y = 0.0;
      return;
    }

    for (let i = 0; i < this._cellViews.length; ++i) {
      const offsetY: number = Cell.HEIGHT * (i - 1);
      this._cellViews[i].view.y = offsetY;
    }
  }

  private _playSymbolsLandAnimations(): void {
    if (this.isHeld) {
      return;
    }

    this._cellViews.forEach((cellView) => cellView.symbol?.playLandAnimation());
  }

  private _resetPosition(): void {
    this._view.y = Cell.getCenteredCellPositionY(this._rowIndex);
  }

  private _playSpinAnimation(reelIndex: number, rowIndex: number): void {
    console.log("CELL PLAY ANIMATION STARTED");
    this._reelStopAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline?.kill();
    this._reelPlayAnimationTimeline = gsap.timeline();

    let totalSpinDuration: number = 0;

    this._createNewCellViewAtIndex(0).createOrRandomizeSymbol();
    this._createNewCellViewAtIndex(2).createOrRandomizeSymbol();

    // this._reelPlayAnimationTimeline.timeScale(0.3);

    this._reelPlayAnimationTimeline
      .to(this._view, {
        y: Cell.getCenteredCellPositionY(this._rowIndex) + Cell.HEIGHT,
        duration: Cell.SPIN_DURATION_INITIAL_S,
        ease: "back.in(1)",
        onComplete: () => {
          this._moveBottomCellViewToTop();
          this._cellViews[0].createOrRandomizeSymbol();
          this._resetPosition();
        },
      })
      .to(this._view, {
        y: Cell.getCenteredCellPositionY(this._rowIndex) + Cell.HEIGHT,
        duration: Cell.SPIN_ITERATION_DURATION_S,
        ease: "none",
        repeat: -1,
        onRepeat: () => {
          this._moveBottomCellViewToTop();
          this._cellViews[0].createOrRandomizeSymbol();
          this._resetPosition();

          if (this._isReadyToQuickstop()) {
            EventSystem.invoke(
              constants.GameEvents.CELL_STOP_REQUESTED,
              reelIndex,
              rowIndex,
            );

            this._reelPlayAnimationTimeline?.kill();
            return;
          }

          const maxDuration: number =
            Cell.SPIN_DURATION_TOTAL_S + Cell.STOP_DELAY_S;

          totalSpinDuration += Cell.SPIN_ITERATION_DURATION_S;

          const shouldStop: boolean = totalSpinDuration >= maxDuration;

          if (this._isReadyToStop() && shouldStop) {
            // console.log(
            //   `cell row ${this._rowIndex}, reel ${this._reelIndex}, is ready to stop`,
            // );
            EventSystem.invoke(
              constants.GameEvents.CELL_STOP_REQUESTED,
              reelIndex,
              rowIndex,
            );
            this._reelPlayAnimationTimeline?.kill();
          }
        },
      });
  }

  private _stopSpinAnimation(reelIndex: number, rowIndex: number) {
    console.log("CELL PLAY ANIMATION ENDED");
    this._releaseCellViewAtIndex(2);

    this._cellViews[0].releaseSymbol();

    const dataReelIndex =
      this._type === "symbol" ? this._reelIndex : gameEngine.config.reels;
    const dataRowIndex =
      this._type === "symbol" ? this._rowIndex : this._reelIndex; // Map visual position to data
    const finalSymbolType: string =
      gameEngine.gameActionData.panelNew[dataReelIndex][dataRowIndex];

    // console.log(`final symbol type is ${finalSymbolType}`);

    // Handle both symbol and special cell types
    if (this._type === "symbol") {
      this._cellViews[0].createSymbol(
        finalSymbolType?.startsWith("100") ? finalSymbolType : "",
      );
    } else {
      this._cellViews[0].createSymbol(finalSymbolType || "");
    }

    this._heldSymbol = this._cellViews[0].symbol;

    this._reelPlayAnimationTimeline?.kill();
    this._reelStopAnimationTimeline?.kill();
    this._reelStopAnimationTimeline = gsap.timeline();

    this._reelStopAnimationTimeline.to(this._view, {
      y: Cell.getCenteredCellPositionY(this._rowIndex) + Cell.HEIGHT,
      duration:
        this._isSlamStopping || this._isSwiftButtonEnabled
          ? Cell.STOP_DURATION_S
          : Cell.SWIFT_STOP_DURATION_S,
      ease: "quad.out(0.2)",
      onComplete: () => {
        this._releaseCellViewAtIndex(1);
        this._resetPosition();

        requestAnimationFrame(() => {
          this._playSymbolsLandAnimations();

          if (this._heldSymbol && this._type === "symbol") {
            Cell._heldSymbolCount++;
            // console.log(
            //   `started holding reel ${reelIndex}, row ${rowIndex}, with current held symbol count ${Cell._heldSymbolCount}`,
            // );
            const symbolGlobalPos = this._heldSymbol.view.toGlobal(new PIXI.Point(0, 0));

            EventSystem.invoke(
              constants.GameEvents.SYMBOL_LANDED,
              this._heldSymbol.type,
              symbolGlobalPos.x,
              symbolGlobalPos.y,
            );
          }

          EventSystem.invoke(
            constants.GameEvents.CELL_STOPPED,
            reelIndex,
            rowIndex,
          );
        });
      },
    });
  }
}
